import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRestaurant } from "./RestaurantContext";
import { apiFetch } from "./api";
import { markReviewsSeen } from "./newReviews";
import RestaurantLogo from "./RestaurantLogo";

type Review = {
  id: number;
  customerName: string;
  rating: number;
  text: string;
  date: string;
  foodRating?: number;
  serviceRating?: number;
  atmosphereRating?: number;
  aiResponse?: string;
  replyText?: string;
  replyDate?: string | null;
  responseTimeDays?: number | null;
  repliedBy?: { id: number; name: string | null; email: string; role: string } | null;
  // Google sync (G3). All null on demo/manual reviews.
  googleReviewId?: string | null;
  removedFromGoogleAt?: string | null;
  editedByCustomerAt?: string | null;
  replyGoogleState?: string | null;
  replyPushError?: string | null;
  replyPolicyViolation?: string | null;
  // When it reached ReviewCounter (G7c), for the "New" tag.
  arrivedAt?: string;
};

type Restaurant = {
  id: number;
  name: string;
  logoUrl?: string;
  logoScale?: number;
  googleLocationId?: string | null;
  googleLocationTitle?: string | null;
  lastReviewSyncAt?: string | null;
};

const Reviews: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  // Shown inline under the AI buttons of that review, not as a pop-up.
  const [aiError, setAiError] = useState<{ reviewId: number; message: string } | null>(null);
  // The drag handle a `resize` CSS property gives you doesn't exist on
  // mobile Safari/Chrome at all -- there's no grip to drag. Auto-growing the
  // textarea to fit its content instead works the same way on every
  // platform and needs no gesture to discover. Re-runs on every replyText
  // change, not just typing, so it also catches AI-suggest/correct filling
  // the box programmatically and the box pre-filling on "edit reply".
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = replyTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [replyText]);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  // Set when a background refresh (tab refocus) finds a reply that wasn't
  // there a moment ago (Fred, 2026-09-18: an admin and manager can have this
  // page open at the same time, and neither auto-updates from the other's
  // changes today). Not real-time, just closes the "I had no idea" gap the
  // next time this tab gets looked at again.
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);

  // Filter and sort state
  const [filterRating, setFilterRating] = useState<number | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'answered' | 'unanswered'>('all');
  const [filterDate, setFilterDate] = useState<'all' | 'oldest' | 'newest'>('all');
  // Year/month filter (Fred, 2026-09-18): independent of each other, so
  // "March, any year" or "2026, any month" both work, not just a combined
  // year+month pair.
  const [filterYear, setFilterYear] = useState<number | 'all'>('all');
  const [filterMonth, setFilterMonth] = useState<number | 'all'>('all');
  // Reviews the customer deleted on Google stay here, marked, but hidden
  // unless asked for (Fred, 2026-10-13).
  const [showRemoved, setShowRemoved] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  // Inline confirmation for deleting a reply (no browser dialog).
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [replyBusyId, setReplyBusyId] = useState<number | null>(null);

  // Get user info from localStorage (set after login)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'superadmin';
  const { currentRestaurantId, loading: restaurantLoading, canWrite } = useRestaurant();

  // Filter and sort reviews
  const getFilteredAndSortedReviews = () => {
    let filtered = showRemoved ? [...reviews] : reviews.filter(review => !review.removedFromGoogleAt);

    // Filter by rating
    if (filterRating !== 'all') {
      filtered = filtered.filter(review => review.rating === filterRating);
    }

    // Filter by status (answered/unanswered)
    if (filterStatus === 'answered') {
      filtered = filtered.filter(review => review.replyText);
    } else if (filterStatus === 'unanswered') {
      filtered = filtered.filter(review => !review.replyText);
    }

    // Filter by year/month, each independent of the other.
    if (filterYear !== 'all') {
      filtered = filtered.filter(review => new Date(review.date).getFullYear() === filterYear);
    }
    if (filterMonth !== 'all') {
      filtered = filtered.filter(review => new Date(review.date).getMonth() === filterMonth);
    }

    // Sort: newest without replies first, then newest with replies (default)
    // OR apply custom date sorting if specified
    if (filterDate === 'all') {
      // Default sorting: unreplied first, then replied, newest first within each group
      filtered.sort((a, b) => {
        const aHasReply = !!a.replyText;
        const bHasReply = !!b.replyText;
        
        // If one has reply and other doesn't, prioritize unreplied
        if (aHasReply !== bHasReply) {
          return aHasReply ? 1 : -1;
        }
        
        // If both have same reply status, sort by date (newest first)
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
    } else if (filterDate === 'newest') {
      // Sort by date only: newest first
      filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else if (filterDate === 'oldest') {
      // Sort by date only: oldest first
      filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    return filtered;
  };

  const filteredReviews = getFilteredAndSortedReviews();
  const removedCount = reviews.filter(review => review.removedFromGoogleAt).length;

  // Years actually present in the data, newest first, so the dropdown never
  // offers a year with nothing in it.
  const availableYears = Array.from(new Set(reviews.map(r => new Date(r.date).getFullYear())))
    .sort((a, b) => b - a);
  // Localized month names (French when the app is in French), reusing the
  // browser's own Intl support instead of hand-translating twelve strings.
  const monthNames = Array.from({ length: 12 }, (_, i) => {
    const name = new Date(2000, i, 1).toLocaleString(i18n.language, { month: 'long' });
    // French month names come back lowercase from Intl; <option> elements
    // don't reliably honor CSS text-transform, so capitalize in JS instead.
    return name.charAt(0).toUpperCase() + name.slice(1);
  });

  // The restaurantId this tab is currently scoped to. Recomputed the same
  // way in the background-refresh effect below, so both stay in agreement
  // about which location's reviews they're comparing.
  const restaurantId = (user.role === 'admin' || user.role === 'manager') ? currentRestaurantId : null;

  // Re-fetches reviews without the full-page loading/error UI, used when the
  // tab regains focus. Diffs the new list against what's already on screen
  // by reply text/status per review id (not a deep-equal on everything, e.g.
  // responseTimeDays legitimately ticks up on its own) and surfaces a small
  // notice if anything actually changed, since this tab has no other way to
  // know someone else just posted or edited a reply.
  const silentlyRefreshReviews = async (query: string) => {
    try {
      const res = await apiFetch(`/api/reviews${query}`, { headers: {} });
      const data = await res.json();
      if (!Array.isArray(data)) return;
      setReviews((prev) => {
        const byId = new Map(prev.map((r) => [r.id, r]));
        const changed = data.filter((next: Review) => {
          const before = byId.get(next.id);
          return before && (before.replyText !== next.replyText);
        });
        if (changed.length === 1 && changed[0].repliedBy?.name) {
          setRefreshNotice(t("reviews.updatedByOne", { name: changed[0].repliedBy.name }));
        } else if (changed.length > 0) {
          setRefreshNotice(t("reviews.updatedByMany", { count: changed.length }));
        }
        return data;
      });
    } catch {
      // A background refresh failing silently is fine - the next focus, or
      // the user's own next action, gets another chance.
    }
  };

  // IMPORTANT: For dev/testing only. In production, use httpOnly cookies for JWTs.
  useEffect(() => {
    // Wait for RestaurantContext's own fetch, but only when we truly have
    // nothing yet - avoids flashing a "no restaurant" error for a brand-new
    // session with no cached selection. A returning user already has
    // currentRestaurantId synchronously from localStorage (RestaurantContext
    // seeds itself from it before the network call resolves), so waiting out
    // the full fetch anyway was one whole extra round-trip serialized in
    // front of this page's own fetch, on every single page load.
    if ((user.role === 'admin' || user.role === 'manager') && restaurantLoading && !currentRestaurantId) return;
    setLoading(true);
    // For an admin or manager with more than one restaurant, ?restaurantId=
    // tells the backend exactly which one - the currently switched-to
    // location, never a silent default that could show the wrong one (or,
    // for a manager, silently merge every location they hold together).
    const query = restaurantId ? `?restaurantId=${restaurantId}` : '';
    apiFetch(`/api/reviews${query}`, {
      headers: {
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setReviews(data);
          setError(null);
        } else if (data && data.error) {
          setError(data.error);
          setReviews([]);
        } else {
          setError(t("reviews.unexpectedResponse"));
          setReviews([]);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(t("reviews.failedToFetch"));
        setReviews([]);
        setLoading(false);
      });

    // Fetch restaurant info (name, logo) for the currently selected location.
    // Managers need this too - previously admin-only, which is why a manager
    // saw the generic "Your Restaurant" placeholder and no logo here even
    // though their reviews were correctly scoped to their real restaurant.
    if ((user.role === 'admin' || user.role === 'manager') && restaurantId) {
      apiFetch(`/api/restaurants/${restaurantId}`, {
        headers: {
        },
      })
        .then((res) => {
          if (res.status === 404) {
            setError(t("reviews.noRestaurantForAdminShort"));
            return null;
          }
          return res.json();
        })
        .then((data) => {
          if (data) setRestaurant(data);
        });
    } else if (user.role === 'admin') {
      setError(t("reviews.noRestaurantForAdmin"));
    }
  }, [currentRestaurantId, restaurantId, restaurantLoading, user.role, t]);

  // Cheapest possible answer to "how would I know someone else just replied
  // while I had this open": no persistent connection, just check again when
  // this tab becomes visible again (switching back to it, restoring a
  // minimized window). Not instant, and does nothing while the tab stays in
  // the background the whole time, but it's zero new backend infrastructure.
  useEffect(() => {
    if (loading || (user.role !== 'admin' && user.role !== 'manager')) return;
    const query = restaurantId ? `?restaurantId=${restaurantId}` : '';
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        silentlyRefreshReviews(query);
        markReviewsSeen().then(setSeenBefore);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, loading, user.role]);

  // Opening the page clears the "New" badge (G7c). What arrived since the
  // previous visit keeps a "New" tag here until the next one.
  const [seenBefore, setSeenBefore] = useState<Date | null>(null);
  useEffect(() => {
    if (user.role !== 'admin' && user.role !== 'manager') return;
    markReviewsSeen().then(setSeenBefore);
  }, [user.role]);
  const isNew = (review: Review) =>
    !!seenBefore && !!review.arrivedAt && !review.replyText && new Date(review.arrivedAt) > seenBefore;

  // Error codes from POST /api/restaurants/:id/google/sync, each translated.
  const SYNC_ERRORS = ['not_linked', 'sync_in_progress', 'sync_cooldown', 'reconnect_required', 'not_connected',
    'google_rate_limited', 'google_forbidden', 'google_not_found', 'google_unavailable'];

  const handleSync = async () => {
    if (!restaurantId) return;
    setSyncing(true);
    setSyncNotice(null);
    try {
      const res = await apiFetch(`/api/restaurants/${restaurantId}/google/sync`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSyncNotice({
          kind: 'error',
          text: SYNC_ERRORS.includes(data.code) ? t(`reviews.sync.errors.${data.code}`) : t('reviews.sync.failed'),
        });
        return;
      }
      setSyncNotice({
        kind: 'ok',
        text: data.added || data.updated || data.removed
          ? t('reviews.sync.result', { added: data.added, updated: data.updated, removed: data.removed })
          : t('reviews.sync.upToDate'),
      });
      setRestaurant((prev) => (prev ? { ...prev, lastReviewSyncAt: data.lastReviewSyncAt } : prev));
      const listRes = await apiFetch(`/api/reviews?restaurantId=${restaurantId}`);
      const list = await listRes.json();
      if (Array.isArray(list)) setReviews(list);
    } catch {
      setSyncNotice({ kind: 'error', text: t('reviews.sync.failed') });
    } finally {
      setSyncing(false);
    }
  };

  const handleReplyClick = (reviewId: number) => {
    setReplyingTo(reviewId);
    setReplyText("");
  };

  // Reopens an already-sent reply for editing, pre-filled with the current
  // text rather than blank (Fred, 2026-09-18: matches Google's own behavior
  // of letting a business edit its reply after posting).
  const handleEditReply = (review: Review) => {
    setReplyingTo(review.id);
    setReplyText(review.replyText || "");
  };

  // Error codes the reply routes can return, each translated.
  const REPLY_ERRORS = ['not_linked', 'review_removed', 'reply_too_long', 'reply_in_progress',
    'reconnect_required', 'not_connected', 'google_rate_limited', 'google_forbidden', 'google_not_found', 'google_unavailable'];
  const replyErrorText = (code: string | null | undefined, fallbackKey: string) =>
    code && REPLY_ERRORS.includes(code) ? t(`reviews.google.errors.${code}`) : t(fallbackKey);

  // Sends (or re-sends) a reply. For a Google review the server answers 200
  // even when Google refused, with the review marked "failed", so the card
  // itself shows what happened and offers the retry.
  const sendReply = async (reviewId: number, text: string) => {
    setReplyBusyId(reviewId);
    try {
      const res = await apiFetch(`/api/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ replyText: text }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(replyErrorText(data.code, "reviews.failedToPostReply"));
        return false;
      }
      // Use the server's own replyDate/responseTimeDays rather than just the
      // text, since an edit recomputes both and a stale local copy would
      // keep showing the original reply's timing after an edit.
      setReviews((prev) =>
        prev.map((r) =>
          r.id === reviewId ? { ...r, ...data.review } : r
        )
      );
      return true;
    } catch (error) {
      alert(t("reviews.failedToPostReply"));
      return false;
    } finally {
      setReplyBusyId(null);
    }
  };

  const handleSendReply = async (reviewId: number) => {
    setAiLoading(true);
    if (await sendReply(reviewId, replyText)) {
      setReplyingTo(null);
      setReplyText("");
    }
    setAiLoading(false);
  };

  const handleDeleteReply = async (reviewId: number) => {
    setReplyBusyId(reviewId);
    try {
      const res = await apiFetch(`/api/reviews/${reviewId}/reply`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(replyErrorText(data.code, "reviews.google.deleteFailed"));
        return;
      }
      setReviews((prev) => prev.map((r) => (r.id === reviewId ? { ...r, ...data.review, repliedBy: null } : r)));
      setConfirmDeleteId(null);
    } catch {
      alert(t("reviews.google.deleteFailed"));
    } finally {
      setReplyBusyId(null);
    }
  };

  const handleAISuggest = async (reviewId: number) => {
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await apiFetch(`/api/reviews/${reviewId}/ai-suggest`, {
        method: "POST",
        headers: {
        },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        // Keep whatever the user already typed.
        setAiError({ reviewId, message: data?.code === "ai_busy" ? t("reviews.aiBusy") : t("reviews.failedAISuggestion") });
        return;
      }
      setReplyText(data.aiReply || replyText);
    } catch (error) {
      setAiError({ reviewId, message: t("reviews.failedAISuggestion") });
    } finally {
      setAiLoading(false);
    }
  };

  const handleAICorrect = async (reviewId: number) => {
    if (!replyText.trim()) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await apiFetch(`/api/reviews/${reviewId}/ai-correct`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draftText: replyText }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setAiError({ reviewId, message: data?.code === "ai_busy" ? t("reviews.aiBusy") : t("reviews.failedAICorrection") });
        return;
      }
      setReplyText(data.correctedReply || replyText);
    } catch (error) {
      setAiError({ reviewId, message: t("reviews.failedAICorrection") });
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) return <div className="p-4 text-center">{t("reviews.loading")}</div>;
  if (error) return <div className="p-4 text-center text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 bg-white shadow z-10 p-4 flex items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <RestaurantLogo
            logoUrl={restaurant?.logoUrl}
            logoScale={restaurant?.logoScale}
            alt="Logo"
            sizeClassName="w-12 h-12"
          />
          <span className="text-xl font-semibold text-teal-700">
            {restaurant?.name || t("reviews.yourRestaurant")}
          </span>
        </div>
      </header>
      <main className="p-4 max-w-md mx-auto">
        <h1 className="text-xl font-bold mb-4 text-center">{t("reviews.recentReviews")}</h1>

        {refreshNotice && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-lg p-3 mb-4 flex items-center justify-between gap-2">
            <span>{refreshNotice}</span>
            <button onClick={() => setRefreshNotice(null)} className="text-blue-600 font-semibold shrink-0">
              {t("common.dismiss")}
            </button>
          </div>
        )}

        {restaurant?.googleLocationId && !isSuperAdmin && (
          <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                <div className="font-medium text-gray-800">{t('reviews.sync.linkedTo', { listing: restaurant.googleLocationTitle || '' })}</div>
                <div>
                  {restaurant.lastReviewSyncAt
                    ? t('reviews.sync.lastSynced', { when: new Date(restaurant.lastReviewSyncAt).toLocaleString(i18n.language) })
                    : t('reviews.sync.neverSynced')}
                </div>
              </div>
              <button
                onClick={handleSync}
                disabled={syncing || !canWrite}
                title={canWrite ? undefined : t('readOnly.disabledHint')}
                className="shrink-0 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {syncing ? t('reviews.sync.syncing') : t('reviews.sync.syncNow')}
              </button>
            </div>
            {syncNotice && (
              <div className={`text-sm rounded p-2 ${syncNotice.kind === 'ok' ? 'bg-teal-50 text-teal-800' : 'bg-red-50 text-red-800'}`}>
                {syncNotice.text}
              </div>
            )}
          </div>
        )}

        {/* Filter Controls */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-1 border rounded text-sm"
            >
              <option value="all">{t("reviews.allRatings")}</option>
              <option value={1}>{t("reviews.starsOne")}</option>
              <option value={2}>{t("reviews.stars", { count: 2 })}</option>
              <option value={3}>{t("reviews.stars", { count: 3 })}</option>
              <option value={4}>{t("reviews.stars", { count: 4 })}</option>
              <option value={5}>{t("reviews.stars", { count: 5 })}</option>
          </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'answered' | 'unanswered')}
              className="px-3 py-1 border rounded text-sm"
            >
              <option value="all">{t("reviews.allReviews")}</option>
              <option value="unanswered">{t("reviews.unanswered")}</option>
              <option value="answered">{t("reviews.answered")}</option>
          </select>

            <select
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value as 'all' | 'oldest' | 'newest')}
              className="px-3 py-1 border rounded text-sm"
            >
            <option value="all">{t("reviews.all")}</option>
              <option value="oldest">{t("reviews.oldestFirst")}</option>
              <option value="newest">{t("reviews.newestFirst")}</option>
          </select>

            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-1 border rounded text-sm"
            >
              <option value="all">{t("reviews.allYears")}</option>
              {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>

            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-3 py-1 border rounded text-sm"
            >
              <option value="all">{t("reviews.allMonths")}</option>
              {monthNames.map((name, i) => (
                <option key={i} value={i}>{name}</option>
              ))}
            </select>
          </div>

          <div className="text-xs text-gray-500">
            {t("reviews.showingCount", { shown: filteredReviews.length, total: reviews.length })}
          </div>
          {removedCount > 0 && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
              {t("reviews.sync.showRemoved", { count: removedCount })}
            </label>
          )}
        </div>
        
        <div className="space-y-4">
          {Array.isArray(filteredReviews) && filteredReviews.map((review) => (
            <div key={review.id} className="bg-white rounded-xl shadow p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">{review.customerName}</span>
                <span className="text-yellow-500 font-bold">{'★'.repeat(review.rating)}</span>
              </div>
              {(review.removedFromGoogleAt || review.editedByCustomerAt || isNew(review)) && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {isNew(review) && (
                    <span className="text-xs font-semibold bg-red-600 text-white rounded px-2 py-0.5">{t("reviews.newTag")}</span>
                  )}
                  {review.removedFromGoogleAt && (
                    <span className="text-xs font-semibold bg-gray-100 text-gray-600 rounded px-2 py-0.5">{t("reviews.sync.removedBadge")}</span>
                  )}
                  {review.editedByCustomerAt && (
                    <span className="text-xs font-semibold bg-amber-50 text-amber-800 rounded px-2 py-0.5">{t("reviews.sync.editedBadge")}</span>
                  )}
                </div>
              )}
              <div className="text-gray-700 mb-2">{review.text}</div>
              <div className="text-xs text-gray-400 mb-1">{new Date(review.date).toLocaleDateString()}</div>
              <div className="flex gap-2 text-xs text-gray-500 mb-2">
                {/* != null, not !== undefined: the API sends null for "not rated",
                    which Google-imported reviews always are. */}
                {review.foodRating != null && <span>{t("reviews.food")}: {review.foodRating}/5</span>}
                {review.serviceRating != null && <span>{t("reviews.service")}: {review.serviceRating}/5</span>}
                {review.atmosphereRating != null && <span>{t("reviews.atmosphere")}: {review.atmosphereRating}/5</span>}
              </div>
              {replyingTo === review.id ? (
                <div className="mt-2">
                  <textarea
                    ref={replyTextareaRef}
                    className="w-full border rounded p-2 mb-2 resize-none overflow-y-auto max-h-64"
                    rows={2}
                    placeholder={t("reviews.writeReply")}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  {review.googleReviewId && new TextEncoder().encode(replyText).length > 4096 && (
                    <div className="text-xs text-red-700 mb-2">{t("reviews.google.errors.reply_too_long")}</div>
                  )}
                  {/* Two uniform-width rows on mobile (AI actions on top, Send/Cancel
                      below), reflowing into the original single row past `sm`.
                      flex-col-reverse + DOM order [send/cancel, AI] puts AI on top
                      on mobile while keeping send/cancel first, as before, on desktop. */}
                  <div className="flex flex-col-reverse gap-2 mb-2 sm:flex-row sm:flex-wrap">
                    <div className="grid grid-cols-2 gap-2 sm:contents">
                      <button
                        className="bg-teal-600 text-white px-4 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleSendReply(review.id)}
                        disabled={!canWrite}
                        title={canWrite ? undefined : t("readOnly.disabledHint")}
                      >
                        {review.googleReviewId
                          ? (review.replyText ? t("reviews.google.updateOnGoogle") : t("reviews.google.publishOnGoogle"))
                          : (review.replyText ? t("reviews.updateReply") : t("reviews.send"))}
                      </button>
                      <button
                        className="bg-gray-300 text-gray-700 px-4 py-1 rounded"
                        onClick={() => setReplyingTo(null)}
                      >
                        {t("reviews.cancel")}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:contents">
                      <button
                        className="bg-purple-600 text-white px-3 py-1 rounded font-semibold shadow hover:bg-purple-700 transition disabled:opacity-50"
                        onClick={() => handleAISuggest(review.id)}
                        disabled={aiLoading || !canWrite}
                        title={canWrite ? undefined : t("readOnly.disabledHint")}
                      >
                        {aiLoading ? t("reviews.thinking") : t("reviews.suggestWithAI")}
                      </button>
                      <button
                        className="bg-purple-100 text-purple-700 px-3 py-1 rounded font-semibold shadow hover:bg-purple-200 transition disabled:opacity-50"
                        onClick={() => handleAICorrect(review.id)}
                        disabled={aiLoading || !canWrite || !replyText.trim()}
                        title={canWrite ? undefined : t("readOnly.disabledHint")}
                      >
                        {aiLoading ? t("reviews.thinking") : t("reviews.correctWithAI")}
                      </button>
                    </div>
                  </div>
                  {aiError?.reviewId === review.id && (
                    <p className="text-xs text-amber-700 mb-2" role="status">{aiError.message}</p>
                  )}
                </div>
              ) : review.replyText ? (
                <div className="bg-teal-50 rounded p-2 mt-2 text-sm text-teal-700">
                  <strong>{t("reviews.yourReply")}</strong> {review.replyText}
                  {review.replyGoogleState === 'sent' && (
                    <span className="ml-2 text-xs font-semibold bg-white text-teal-800 rounded px-2 py-0.5">{t("reviews.sync.onGoogle")}</span>
                  )}
                  {review.replyGoogleState === 'pending_moderation' && (
                    <div className="mt-2 text-xs bg-amber-50 text-amber-800 rounded p-2">{t("reviews.google.pending")}</div>
                  )}
                  {review.replyGoogleState === 'rejected' && (
                    <div className="mt-2 text-xs bg-red-50 text-red-800 rounded p-2">
                      {t("reviews.google.rejected", {
                        reason: (review.replyPolicyViolation || 'GENERIC_VIOLATION').toLowerCase().replace(/_/g, ' '),
                      })}
                    </div>
                  )}
                  {review.replyGoogleState === 'failed' && (
                    <div className="mt-2 text-xs bg-red-50 text-red-800 rounded p-2 flex items-center justify-between gap-2">
                      <span>
                        <strong>{t("reviews.google.notSent")}</strong>{' '}
                        {review.replyPushError && review.replyPushError !== 'not_sent_yet' && replyErrorText(review.replyPushError, "reviews.google.notSentGeneric")}
                      </span>
                      {!isSuperAdmin && (
                        <button
                          className="shrink-0 bg-red-600 text-white px-3 py-1 rounded font-semibold disabled:opacity-50"
                          onClick={() => sendReply(review.id, review.replyText || '')}
                          disabled={!canWrite || replyBusyId === review.id}
                        >
                          {replyBusyId === review.id ? t("reviews.google.sending") : t("reviews.google.retry")}
                        </button>
                      )}
                    </div>
                  )}
                    {review.replyDate && (
                    <div className="text-xs text-teal-600 mt-1">
                      {t("reviews.repliedOn", { date: new Date(review.replyDate).toLocaleDateString() })}
                      {review.responseTimeDays !== null && (
                        <span className="ml-2">
                          {t("reviews.responseTime", { days: review.responseTimeDays })}
                      </span>
                    )}
                  </div>
                    )}
                  {/* Google itself lets a business edit its reply after
                      posting, so locking ours forever once sent would be a
                      step backward from that, not just a local limitation. */}
                  {!isSuperAdmin && (
                    <button
                      className="mt-2 text-teal-700 underline text-xs disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                      onClick={() => handleEditReply(review)}
                      disabled={!canWrite}
                      title={canWrite ? undefined : t("readOnly.disabledHint")}
                    >
                      {t("reviews.editReply")}
                    </button>
                  )}
                  {!isSuperAdmin && confirmDeleteId !== review.id && (
                    <button
                      className="mt-2 ml-4 text-red-700 underline text-xs disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                      onClick={() => setConfirmDeleteId(review.id)}
                      disabled={!canWrite}
                      title={canWrite ? undefined : t("readOnly.disabledHint")}
                    >
                      {t("reviews.google.deleteReply")}
                    </button>
                  )}
                  {confirmDeleteId === review.id && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-800 space-y-2">
                      <div>{review.googleReviewId ? t("reviews.google.deleteWarningGoogle") : t("reviews.google.deleteWarningLocal")}</div>
                      <div className="flex gap-2">
                        <button
                          className="bg-red-600 text-white px-3 py-1 rounded font-semibold disabled:opacity-50"
                          onClick={() => handleDeleteReply(review.id)}
                          disabled={replyBusyId === review.id}
                        >
                          {t("reviews.google.confirmDelete")}
                        </button>
                        <button className="bg-white text-gray-700 px-3 py-1 rounded" onClick={() => setConfirmDeleteId(null)}>
                          {t("reviews.cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                  </div>
              ) : (
                // Only show Reply button if not superadmin, and never on a
                // review the customer removed from Google (nothing to answer).
                !isSuperAdmin && !(review.googleReviewId && review.removedFromGoogleAt) && (
                  <button
                    className="mt-2 bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-teal-600"
                    onClick={() => handleReplyClick(review.id)}
                    disabled={!canWrite}
                    title={canWrite ? undefined : t("readOnly.disabledHint")}
                  >
                    {t("reviews.reply")}
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Reviews;